import { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { getSalonById, updateSalon, deleteSalon } from "../api/salons";
import { resolveAssetUrl } from "../utils/assetUrl";



const AMENITY_OPTIONS = ["WiFi", "Parking", "Washer", "Dryer", "Reception", "Backbar"];



const emptyForm = {

  name: "",

  description: "",

  address: "",

  city: "",

  province: "",

  postal_code: "",

  latitude: "",

  longitude: "",

  default_buffer_minutes: "",

};



export default function EditSalon() {

  const { id } = useParams();

  const navigate = useNavigate();



  const [form, setForm] = useState(emptyForm);

  const [selectedAmenities, setSelectedAmenities] = useState([]);

  const [otherAmenities, setOtherAmenities] = useState([]);

  const [otherAmenityInput, setOtherAmenityInput] = useState("");

  const [rulesText, setRulesText] = useState("");

  const [bufferMinutes, setBufferMinutes] = useState("");



  const [existingPhotos, setExistingPhotos] = useState([]);

  const [photosToRemove, setPhotosToRemove] = useState([]);

  const [newPhotos, setNewPhotos] = useState([]);



  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");



  useEffect(() => {

    const fetchSalon = async () => {

      setLoading(true);

      setError("");

      try {

        const res = await getSalonById(id);

        const data = res?.data || res;



        const amenities = data.amenities || [];

        const selected = AMENITY_OPTIONS.filter((opt) =>

          amenities.some((a) => a && a.toLowerCase() === opt.toLowerCase())

        );

        const others = amenities.filter(

          (a) =>

            a &&

            !selected.some((opt) => opt.toLowerCase() === a.toLowerCase())

        );



        setForm({

          name: data.name || "",

          description: data.description || "",

          address: data.address || "",

          city: data.city || "",

          province: data.province || "",

          postal_code: data.postal_code || "",

          latitude:

            data.latitude === null || data.latitude === undefined

              ? ""

              : String(data.latitude),

          longitude:

            data.longitude === null || data.longitude === undefined

              ? ""

              : String(data.longitude),

          default_buffer_minutes:

            data.default_buffer_minutes === null ||

            data.default_buffer_minutes === undefined

              ? ""

              : String(data.default_buffer_minutes),

        });



        setBufferMinutes(

          data.default_buffer_minutes === null ||

          data.default_buffer_minutes === undefined

            ? ""

            : String(data.default_buffer_minutes)

        );

        setSelectedAmenities(selected);

        setOtherAmenities(others);

        setRulesText((data.rules || []).join(", "));

        setExistingPhotos(data.photos || []);

      } catch (err) {

        console.error("Failed to load salon", err);

        setError("Failed to load salon details.");

      } finally {

        setLoading(false);

      }

    };



    fetchSalon();

  }, [id]);



  const handleChange = (e) => {

    const { name, value } = e.target;

    setForm((prev) => ({

      ...prev,

      [name]: value,

    }));

  };



  const toggleAmenity = (amenity) => {

    setSelectedAmenities((prev) =>

      prev.includes(amenity)

        ? prev.filter((a) => a !== amenity)

        : [...prev, amenity]

    );

  };



  const addOtherAmenity = () => {

    const trimmed = otherAmenityInput.trim();

    if (!trimmed) return;

    if (!otherAmenities.includes(trimmed)) {

      setOtherAmenities((prev) => [...prev, trimmed]);

    }

    setOtherAmenityInput("");

  };



  const removeOtherAmenity = (name) => {

    setOtherAmenities((prev) => prev.filter((a) => a !== name));

  };



  const handleNewPhotosChange = (e) => {

    const files = Array.from(e.target.files || []);

    setNewPhotos(files);

  };



  const toggleRemovePhoto = (url) => {

    setPhotosToRemove((prev) =>

      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]

    );

  };



  const handleSave = async (e) => {

    e.preventDefault();

    setError("");



    if (!form.name.trim()) {

      setError("Name is required.");

      return;

    }

    if (!form.address.trim()) {

      setError("Address is required.");

      return;

    }

    if (!form.city.trim()) {

      setError("City is required.");

      return;

    }

    if (!form.province.trim()) {

      setError("Province is required.");

      return;

    }



    let latitude = null;

    let longitude = null;

    if (form.latitude && form.latitude.trim() !== "") {

      const val = Number(form.latitude);

      if (!Number.isNaN(val)) latitude = val;

    }

    if (form.longitude && form.longitude.trim() !== "") {

      const val = Number(form.longitude);

      if (!Number.isNaN(val)) longitude = val;

    }



    let defaultBufferMinutes = null;

    if (bufferMinutes !== "") {

      const parsed = Number(bufferMinutes);

      if (Number.isNaN(parsed) || parsed < 0) {

        setError("Default buffer minutes must be a non-negative number.");

        return;

      }

      defaultBufferMinutes = parsed;

    }



    const amenitySet = new Set([...selectedAmenities, ...otherAmenities]);

    const amenities = Array.from(amenitySet);



    const rules =

      rulesText

        .split(",")

        .map((r) => r.trim())

        .filter(Boolean) || [];



    const payload = {

      name: form.name.trim(),

      description: form.description.trim() || null,

      address: form.address.trim(),

      city: form.city.trim(),

      province: form.province.trim(),

      postal_code: form.postal_code.trim() || null,

      latitude,

      longitude,

      amenities,

      rules,

      default_buffer_minutes: defaultBufferMinutes,

    };



    setSaving(true);

    try {

      await updateSalon(id, payload, newPhotos, photosToRemove);

      navigate("/my-salons");

    } catch (err) {

      console.error("Failed to update salon", err);

      const message =

        err?.response?.data?.detail ||

        err?.message ||

        "Failed to update salon. Please try again.";

      setError(message);

    } finally {

      setSaving(false);

    }

  };



  const handleDelete = async () => {

    if (

      !window.confirm(

        "Are you sure you want to delete this salon? This action cannot be undone."

      )

    ) {

      return;

    }

    try {

      await deleteSalon(id);

      navigate("/my-salons");

    } catch (err) {

      console.error("Failed to delete salon", err);

      setError("Failed to delete salon.");

    }

  };



  if (loading) {

    return <div className="p-6">Loading...</div>;

  }



  return (

    <div className="max-w-4xl mx-auto p-6">

      <div className="flex justify-between items-center mb-4">

        <h1 className="text-3xl font-semibold">Edit Salon</h1>

        <button

          type="button"

          onClick={handleDelete}

          className="bg-red-600 text-white px-4 py-2 rounded-lg"

        >

          Delete Salon

        </button>

      </div>



      {error && (

        <div className="mb-4 rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">

          {error}

        </div>

      )}



      <form onSubmit={handleSave} className="space-y-6">

        {/* Basic details */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          <input

            name="name"

            value={form.name}

            onChange={handleChange}

            placeholder="Name"

            className="border p-2 rounded"

          />

          <input

            name="address"

            value={form.address}

            onChange={handleChange}

            placeholder="Address"

            className="border p-2 rounded"

          />

          <input

            name="city"

            value={form.city}

            onChange={handleChange}

            placeholder="City"

            className="border p-2 rounded"

          />

          <input

            name="province"

            value={form.province}

            onChange={handleChange}

            placeholder="Province"

            className="border p-2 rounded"

          />

          <input

            name="postal_code"

            value={form.postal_code}

            onChange={handleChange}

            placeholder="Postal Code"

            className="border p-2 rounded"

          />

          <input

            name="latitude"

            value={form.latitude}

            onChange={handleChange}

            placeholder="Latitude (optional)"

            className="border p-2 rounded"

          />

          <input

            name="longitude"

            value={form.longitude}

            onChange={handleChange}

            placeholder="Longitude (optional)"

            className="border p-2 rounded"

          />

        </div>



        {/* Description */}

        <div>

          <textarea

            name="description"

            value={form.description}

            onChange={handleChange}

            placeholder="Describe your salon"

            className="w-full border p-2 rounded"

            rows={4}

          />

        </div>



        {/* Default buffer */}

        <div>

          <label className="block text-sm font-medium mb-1">

            Default cleaning buffer between bookings (minutes)

          </label>

          <input

            type="number"

            value={bufferMinutes}

            onChange={(e) => setBufferMinutes(e.target.value)}

            className="border p-2 rounded w-full max-w-xs"

            min="0"

          />

          <p className="text-xs text-slate-500 mt-1">

            This buffer will be suggested for new booths created under this

            salon. You can still override it at the booth level.

          </p>

        </div>



        {/* Amenities & rules */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Amenities */}

          <div>

            <p className="font-medium mb-2">Amenities</p>

            <div className="flex flex-wrap gap-2 mb-3">

              {AMENITY_OPTIONS.map((amenity) => (

                <button

                  key={amenity}

                  type="button"

                  onClick={() => toggleAmenity(amenity)}

                  className={`px-3 py-1 rounded-full border text-xs ${

                    selectedAmenities.includes(amenity)

                      ? "bg-emerald-600 text-white border-emerald-600"

                      : "bg-white text-gray-700 border-gray-300"

                  }`}

                >

                  {amenity}

                </button>

              ))}

            </div>



            {/* Other amenities */}

            <div className="flex gap-2 mb-2">

              <input

                type="text"

                value={otherAmenityInput}

                onChange={(e) => setOtherAmenityInput(e.target.value)}

                placeholder="Other amenity"

                className="border p-2 rounded flex-1 text-sm"

              />

              <button

                type="button"

                onClick={addOtherAmenity}

                className="bg-gray-800 text-white px-3 py-2 rounded text-sm"

              >

                Add

              </button>

            </div>

            {otherAmenities.length > 0 && (

              <div className="flex flex-wrap gap-2 mt-1">

                {otherAmenities.map((name) => (

                  <span

                    key={name}

                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-xs"

                  >

                    {name}

                    <button

                      type="button"

                      onClick={() => removeOtherAmenity(name)}

                      className="text-gray-500 hover:text-gray-800"

                    >

                      ×

                    </button>

                  </span>

                ))}

              </div>

            )}

          </div>



          {/* Rules */}

          <div>

            <p className="font-medium mb-1">Rules (comma separated)</p>

            <textarea

              value={rulesText}

              onChange={(e) => setRulesText(e.target.value)}

              placeholder="e.g. No smoking, No pets, Arrive 10 minutes early"

              className="w-full border p-2 rounded text-sm"

              rows={5}

            />

            <p className="text-xs text-slate-500 mt-1">

              These rules will be shown to technicians before they book a

              workspace in your salon.

            </p>

          </div>

        </div>



        {/* Photos */}

        <div>

          <p className="font-medium mb-1">Photos</p>



          {existingPhotos && existingPhotos.length > 0 ? (

            <div className="flex flex-wrap gap-4 mb-3">

              {existingPhotos.map((url) => {

                const markedForRemoval = photosToRemove.includes(url);

                return (

                  <div key={url} className="flex flex-col items-start gap-1">

                    <img

                      src={

                        url.startsWith("/static")

                          ? resolveAssetUrl(url)

                          : url

                      }

                      alt="Salon"

                      className="w-32 h-24 object-cover rounded border"

                    />

                    <label className="text-xs flex items-center gap-1">

                      <input

                        type="checkbox"

                        checked={markedForRemoval}

                        onChange={() => toggleRemovePhoto(url)}

                      />

                      Remove

                    </label>

                  </div>

                );

              })}

            </div>

          ) : (

            <p className="text-xs text-slate-500 mb-2">

              No photos uploaded yet.

            </p>

          )}



          <input

            type="file"

            multiple

            accept="image/*"

            onChange={handleNewPhotosChange}

            className="block mt-1"

          />

          <p className="text-xs text-slate-500 mt-1">

            New photos will be added; removed photos will be deleted.

          </p>

        </div>



        {/* Actions */}

        <div className="flex gap-3 mt-6">

          <button

            type="submit"

            disabled={saving}

            className="bg-emerald-600 text-white px-4 py-2 rounded-lg disabled:opacity-60"

          >

            {saving ? "Saving..." : "Save Changes"}

          </button>



          <button

            type="button"

            onClick={() => navigate(-1)}

            className="border border-gray-300 px-4 py-2 rounded-lg"

          >

            Cancel

          </button>

        </div>

      </form>

    </div>

  );

}

